// THE CALLER'S HALF of Anna's rich-context handshake.
//
// ---------------------------------------------------------------------------
// WHY THE DEMO WRITES THIS AND THE SDK DOES NOT
// ---------------------------------------------------------------------------
// `@anshinus/anna-sdk-react` ACCEPTS a `contextRef` and puts it in the launch URL
// as `?context_ref=<uuid>`. It does not OBTAIN one — there is no prep-session client in
// the published artifact (`grep -ao "prep-session" dist/index.mjs` returns nothing, still
// true on 0.7.x; the string appears only in the `.d.ts` doc comment telling the integrator
// to do this).
//
// So every integrator writes this call. That makes it demo-app code, not SDK code, and
// it makes it worth exercising here: a host application that gets this wrong gets a
// launcher that opens Anna with no rich context and no error, which is the SDK's
// documented degraded behaviour and therefore indistinguishable from working.
//
// Contract read from the product source on 2026-10-19 — anshin-anna
// src/app/api/caller/prep-session/route.ts:33-130 and
// src/lib/anna/context/types.ts:47-58:
//
//   POST {base}/api/caller/prep-session
//   auth   a valid Anshin Connect session; 401 {"error":"unauthorized"} without one
//   body   { tenant_id: <uuid>, src: <string>, rich_context: {...} }
//   201    { context_ref: <uuid>, expires_at: <iso8601>, ttl_seconds: 300 }
//   400    malformed_body | tenant_id_not_uuid | phi_refused  (PHI scrubber, fail-closed)
//   503    prep_session_backend_unavailable
//
// ---------------------------------------------------------------------------
// TWO CROSS-ORIGIN CONSTRAINTS, NEITHER VERIFIED FROM HERE
// ---------------------------------------------------------------------------
// The demo is served from a DIFFERENT origin than Anna, so this call is cross-origin:
//
//  1. It needs the Connect session cookie, hence `credentials: 'include'` — which in
//     turn needs `SameSite=None; Secure` on that cookie.
//  2. It needs CORS. `Access-Control-Allow-Origin` does not appear anywhere in the
//     product's prep-session route or middleware (grepped 2026-10-19), and
//     `credentials: 'include'` additionally requires `Access-Control-Allow-Credentials:
//     true` and an EXACT origin — a wildcard is refused by the browser for credentialed
//     requests.
//
// Both are READ FROM SOURCE AND UNVERIFIED AGAINST THE RUNNING SERVICE. This session
// cannot reach a browser pointed at the deployed product, so the realistic expectation is
// that this call is blocked by CORS on first contact and the demo falls back to
// query-param-only context. The function is written so that outcome is REPORTED, not
// absorbed: a blocked fetch rejects, and `kind: 'network'` carries the message to screen.
//
// ---------------------------------------------------------------------------
// NO FABRICATED DEFAULTS
// ---------------------------------------------------------------------------
// Every failure path keeps the upstream evidence — the HTTP status and the body Anna
// actually sent. Nothing here invents a value to stand in for an answer that did not
// arrive. A `?? 300` on a missing `ttl_seconds` would put a countdown on screen for a
// refusal that has no TTL, and the tester would read the number as proof the call worked.

/** Anna's `RichContext`. Deliberately loose — the product's PHI scrubber is the authority. */
export interface DemoRichContext {
  summary?: string;
  entity_refs?: string[];
  structured?: Record<string, unknown>;
}

export interface PrepSessionOk {
  kind: 'ok';
  contextRef: string;
  expiresAt: string;
  ttlSeconds: number;
}

/** Anna answered, and refused. `status` and `body` are hers, verbatim. */
export interface PrepSessionRefused {
  kind: 'refused';
  status: number;
  /** Anna's `error` field when present, else the raw body, truncated. */
  detail: string;
}

/** The request never produced an HTTP answer — CORS, DNS, TLS, offline. */
export interface PrepSessionNetworkError {
  kind: 'network';
  detail: string;
}

/** Anna answered 2xx with a body that does not satisfy the contract. */
export interface PrepSessionMalformed {
  kind: 'malformed';
  status: number;
  detail: string;
}

export type PrepSessionResult =
  | PrepSessionOk
  | PrepSessionRefused
  | PrepSessionNetworkError
  | PrepSessionMalformed;

/** The product's own UUID shape check, mirrored so a bad tenant id fails here first. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const PREP_SESSION_PATH = '/api/caller/prep-session';

/** Build the prep-session endpoint for an origin. Exported so one definition is tested. */
export function prepSessionUrl(baseUrl: string): string {
  return `${baseUrl}${PREP_SESSION_PATH}`;
}

/**
 * Is this a `context_ref` the launcher can be given?
 *
 * Checked because the SDK does not check it. `buildAnnaUrl` puts whatever it is handed
 * into `?context_ref=`, and Anna's consume endpoint validates server-side — so a
 * malformed ref produces a launch that silently has no rich context, which is the same
 * observable outcome as not passing one at all. Validating here is what makes the two
 * distinguishable on screen.
 */
export function isContextRef(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export interface PrepSessionArgs {
  baseUrl: string;
  tenantId: string;
  src: string;
  richContext: DemoRichContext;
  /** Injected so the unit test needs no network and no browser. */
  fetchImpl?: typeof fetch;
}

/**
 * POST the caller context and return the opaque ref, or the reason there isn't one.
 *
 * Never throws. The caller is a click handler in a demo; an unhandled rejection there
 * shows the tester a blank panel and sends the real error to a console they are not
 * reading.
 */
export async function prepSession(args: PrepSessionArgs): Promise<PrepSessionResult> {
  const { baseUrl, tenantId, src, richContext } = args;
  const doFetch = args.fetchImpl ?? fetch;

  if (!UUID_RE.test(tenantId)) {
    // Caught locally rather than sent, because the product's answer would be a 400 whose
    // body says `tenant_id_not_uuid` — the same information, one round trip and one audit
    // row later. Anna audits every refusal, and filling her audit chain with the demo's
    // own misconfiguration is noise in somebody else's evidence.
    return { kind: 'refused', status: 0, detail: `tenant_id is not a UUID: ${JSON.stringify(tenantId)}` };
  }

  let res: Response;
  try {
    res = await doFetch(prepSessionUrl(baseUrl), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // The Connect session cookie is the auth. Without this the call is anonymous and
      // Anna answers 401 — and because 401 is also what an expired session produces, the
      // tester cannot tell a missing flag from a missing login.
      credentials: 'include',
      body: JSON.stringify({ tenant_id: tenantId, src, rich_context: richContext }),
    });
  } catch (err) {
    return { kind: 'network', detail: err instanceof Error ? err.message : String(err) };
  }

  const text = await res.text().catch(() => '');
  if (!res.ok) {
    return { kind: 'refused', status: res.status, detail: extractError(text) };
  }

  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return { kind: 'malformed', status: res.status, detail: `body is not JSON: ${truncate(text)}` };
  }
  const rec = body as Record<string, unknown>;
  if (!isContextRef(rec.context_ref)) {
    return {
      kind: 'malformed',
      status: res.status,
      detail: `context_ref missing or not a UUID: ${truncate(text)}`,
    };
  }
  if (typeof rec.expires_at !== 'string' || typeof rec.ttl_seconds !== 'number') {
    // Reported rather than filled in. These two fields are what tell a tester how long
    // the ref is good for; substituting the contract's documented 300 would display a
    // deadline the server never committed to.
    return {
      kind: 'malformed',
      status: res.status,
      detail: `expires_at/ttl_seconds missing or wrong type: ${truncate(text)}`,
    };
  }
  return {
    kind: 'ok',
    contextRef: rec.context_ref,
    expiresAt: rec.expires_at,
    ttlSeconds: rec.ttl_seconds,
  };
}

/** Anna's `error` field if the body is JSON with one; otherwise the raw body. */
function extractError(text: string): string {
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const err = parsed.error;
    const detail = parsed.detail;
    if (typeof err === 'string') {
      return typeof detail === 'string' ? `${err}: ${detail}` : err;
    }
  } catch {
    /* not JSON — fall through and show what arrived */
  }
  return truncate(text) || '(empty body)';
}

function truncate(s: string, max = 300): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}
